
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, X, Link, ArrowLeft, Check, Copy } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Member } from "@/types/member";
import { ReceiptScanner } from "@/components/receipts/ReceiptScanner";
import { useGroupReceipts } from "@/hooks/useGroupReceipts";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { safeInsert, safeSelectArray } from "@/utils/supabaseHelpers";

interface GroupDetailsProps {
  groupId: string;
  groupName: string;
  members: Member[];
  showAddMember: boolean;
  showShareLink: boolean;
  handleRemoveMember: (memberId: string) => Promise<void>;
  setShowAddMember: (show: boolean) => void;
  setShowShareLink: (show: boolean) => void;
}

interface SearchUser {
  id: string;
  username: string;
  name: string;
  avatar_url: string;
}

export const GroupDetails = ({
  groupId,
  groupName,
  members,
  showAddMember,
  showShareLink,
  handleRemoveMember,
  setShowAddMember,
  setShowShareLink
}: GroupDetailsProps) => {
  const { t, i18n } = useTranslation();
  const [usernameInput, setUsernameInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [session, setSession] = useState<any>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  const { 
    isOpen, 
    closeScanner, 
    processReceipt,
    isLoadingAPI
  } = useGroupReceipts(groupId);
  
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
    });
  }, []);
  
  const searchUsers = async (username: string) => {
    if (!username.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, name, avatar_url')
        .or(`username.ilike.%${username}%,name.ilike.%${username}%`)
        .order('username')
        .limit(5);
      
      if (error) {
        console.error('Error searching users:', error);
        toast.error(t('groups.searchError'));
        setSearchResults([]);
      } else {
        const validResults = (data || []).map(user => ({
          id: user.id || '',
          username: user.username || '',
          name: user.name || 'Unnamed User',
          avatar_url: user.avatar_url || ''
        }));
        setSearchResults(validResults);
      }
    } catch (error) {
      console.error('Error in searchUsers:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddMember = async (userId: string) => {
    try {
      const result = await supabase
        .from('group_members')
        .insert({
          group_id: groupId,
          profile_id: userId,
          is_admin: false
        })
        .select('*');
      
      if (result.error) {
        console.error('Error adding member:', result.error);
        toast.error(t('groups.addMemberError'));
        return;
      }
      
      toast.success(t('groups.memberAddedSuccess'));
      setUsernameInput("");
      setSearchResults([]);
    } catch (error) {
      console.error('Error in handleAddMember:', error);
      toast.error(t('groups.addMemberError'));
    }
  };

  const handleCapturedReceipt = (imageSrc: string) => {
    processReceipt(imageSrc);
  };

  const shareLink = `/invite/${groupId}`;
  const fullShareLink = `${window.location.origin}${shareLink}`;
  
  const copyShareLink = () => {
    navigator.clipboard.writeText(fullShareLink);
    
    // Log to confirm function is being called
    console.log("Copying link and showing toast");
    
    // Show toast notification using Sonner with position at the top and 2 second duration
    toast.success(t('common.copy'), {
      description: t('groups.linkCopied'),
      position: "top-center",
      icon: <Check className="h-4 w-4 text-white" />,
      className: "bg-gradient-to-r from-[#5770fd] to-[#ff66c4] text-white",
      duration: 2000, // 2 seconds
    });
  };

  const handleInviteClick = () => {
    // Add logic for inviting members
  };

  return (
    <>
      <Card className="backdrop-blur-sm bg-white/30 dark:bg-black/30 border border-gray-200/50 dark:border-gray-800/50 shadow-md">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/app")} 
              className="p-1"
              aria-label={t('common.back')}
            >
              <ArrowLeft className="h-5 w-5 text-[#5770fd]" />
            </Button>
            <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-gray-100">
              {groupName}
            </h1>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-lg font-semibold whitespace-nowrap text-gray-800 dark:text-gray-200">{t('groups.groupMembers')}</h2>
              <div className="flex gap-1 sm:gap-2 ml-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAddMember(!showAddMember)} 
                  className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3"
                >
                  {showAddMember ? <X className="h-4 w-4" /> : <X className="h-4 w-4 rotate-45" />}
                  <span className="hidden sm:inline">{showAddMember ? t('groups.cancel') : t('groups.addMember')}</span>
                  <span className="sm:hidden">{showAddMember ? t('groups.cancel') : t('groups.add')}</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowShareLink(!showShareLink)} 
                  className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3"
                >
                  <Link className="h-4 w-4" />
                  <span>{t('groups.invite')}</span>
                </Button>
              </div>
            </div>

            {showShareLink && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-white/50 dark:bg-black/50">
                <input
                  type="text"
                  value={fullShareLink}
                  readOnly
                  className="flex-1 text-sm bg-transparent border rounded px-2 py-1"
                />
                <Button 
                  size="sm" 
                  onClick={copyShareLink}
                  className="flex items-center gap-1"
                >
                  <Copy className="h-4 w-4" />
                  {t('common.copy')}
                </Button>
              </div>
            )}

            {showAddMember && (
              <div className="space-y-2 p-2 rounded-lg bg-white/50 dark:bg-black/50">
                <div className="flex gap-2">
                  <input
                    placeholder={t('groups.searchPlaceholder')}
                    value={usernameInput}
                    onChange={e => {
                      setUsernameInput(e.target.value);
                      searchUsers(e.target.value);
                    }}
                    className="flex-1 border rounded px-2 py-1 text-sm"
                  />
                </div>
                {isSearching ? (
                  <div className="flex justify-center p-2">
                    <svg className="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : searchResults.length > 0 && (
                  <div className="space-y-2">
                    {searchResults.map(user => (
                      <div key={user.id} className="flex items-center justify-between p-2 rounded-lg bg-white/30 dark:bg-black/30 backdrop-blur-sm animate-fade-in">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar_url || undefined} />
                            <AvatarFallback className="bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                              {user.name?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="font-medium text-sm text-gray-800 dark:text-gray-200">
                              {user.name || t('groups.unnamed')}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              @{user.username?.split('@')[0]}
                            </span>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => handleAddMember(user.id)} className="ml-2">
                          {t('groups.add')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              {members.map(member => {
                const isCurrentUserAdmin = members.find(m => m.id === session?.user?.id && m.isAdmin);
                return (
                  <div key={member.id} className="flex items-center justify-between p-2 rounded-lg bg-white/50 dark:bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.avatar_url || undefined} />
                        <AvatarFallback>{member.name.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-sm text-gray-800 dark:text-gray-200">
                          {member.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          @{member.username}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.isAdmin && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-3 w-3 mr-1 text-amber-500" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <path d="M12 2l1.964 5.747h6.352l-5.14 3.734 1.964 5.747L12 13.495l-5.14 3.733 1.963-5.747-5.14-3.734h6.352L12 2z"/>
                          </svg>
                          {t('groups.admin')}
                        </Badge>
                      )}
                      {isCurrentUserAdmin && member.id !== session?.user?.id && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveMember(member.id)}
                          className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <ReceiptScanner 
        open={isOpen}
        onClose={closeScanner}
        onCapture={handleCapturedReceipt}
        isProcessing={isLoadingAPI}
      />
    </>
  );
};
